import { fetchWithAuth } from "@/lib/api-client";

export type JurisdictionLevel =
  | "federal"
  | "state"
  | "county"
  | "municipality"
  | "special_district";

export type JurisdictionLayer = {
  level: JurisdictionLevel;
  name: string;
  jurisdiction_code: string | null;
  source_url: string | null;
  in_scope: boolean;
};

export type ResolveResult = {
  address: string;
  normalized_address: string | null;
  lat: number | null;
  lng: number | null;
  layers: JurisdictionLayer[];
};

export const LEVEL_LABELS: Record<JurisdictionLevel, string> = {
  federal: "Federal",
  state: "State",
  county: "County",
  municipality: "Municipality",
  special_district: "Special District",
};

export const SAMPLE_ADDRESSES = [
  "801 Plum St, Cincinnati, OH 45202",
  "20 W Pike St, Covington, KY 41011",
];

/**
 * Resolve an address to its jurisdiction stack. Works anonymously
 * (the backend endpoint is public, IP rate-limited); when a session
 * token is present fetchWithAuth attaches it automatically.
 */
export async function resolveJurisdiction(
  address: string,
): Promise<ResolveResult> {
  const res = await fetchWithAuth("/jurisdiction/resolve", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      typeof payload?.detail === "string"
        ? payload.detail
        : res.status === 429
          ? "You've made a lot of lookups in a short time. Please wait a moment and try again."
          : "Could not look up that address. Please try again.";
    throw new Error(detail);
  }
  return payload as ResolveResult;
}
